import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ClientsPage from './pages/ClientsPage';
import ClientDetailPage from './pages/ClientDetailPage';
import QuotesPage from './pages/QuotesPage';
import QuoteDetailPage from './pages/QuoteDetailPage';
import PaymentsPage from './pages/PaymentsPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import InvoicesPage from './pages/InvoicesPage';
import InvoiceDetailPage from './pages/InvoiceDetailPage';
import LeadsPage from './pages/LeadsPage';
import WorkOrdersPage from './pages/WorkOrdersPage';
import WorkOrderDetailPage from './pages/WorkOrderDetailPage';
import InventoryPage from './pages/InventoryPage';
import InventoryDetailPage from './pages/InventoryDetailPage';
import CalendarPage from './pages/CalendarPage';
import TechniciansPage from './pages/TechniciansPage';
import ReportsPage from './pages/ReportsPage';
import UserManagementPage from './pages/UserManagementPage';
import ClientPortalPage from './pages/ClientPortalPage';
import ClientLoginPage from './pages/ClientLoginPage';
import CatalogPage from './pages/CatalogPage';
import PayrollPage from './pages/PayrollPage';
import DocumentsPage from './pages/DocumentsPage';
import PaymentLinksPage from './pages/PaymentLinksPage';
import TechDashboardPage from './pages/TechDashboardPage';
import ManualPage from './pages/ManualPage';
import AIAssistantPage from './pages/AIAssistantPage';
import OutreachSafetyPage from './pages/OutreachSafetyPage';
import CampaignMetricsPage from './pages/CampaignMetricsPage';
import LeadGenSeoPage from './pages/LeadGenSeoPage';
import QuoteSignPage from './pages/QuoteSignPage';
import WorkOrderPublicPage from './pages/WorkOrderPublicPage';
import PipelinePage from './pages/PipelinePage';
import ProspectsPage from './pages/ProspectsPage';
import CallCandidatesPage from './pages/CallCandidatesPage';
import ProspectDetailPage from './pages/ProspectDetailPage';
import CampaignsPage from './pages/CampaignsPage';
import CampaignDetailPage from './pages/CampaignDetailPage';
import B2BReportsPage from './pages/B2BReportsPage';
import MarketIntelPage from './pages/MarketIntelPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import ActivityPage from './pages/ActivityPage';
import GPSTrackingPage from './pages/GPSTrackingPage';
import BookingPage from './pages/BookingPage';
import HRMSPage from './pages/HRMSPage';
import ExecutiveDashboardPage from './pages/ExecutiveDashboardPage';
import B2BAutoPage from './pages/B2BAutoPage';
import B2BUnifiedPage from './pages/B2BUnifiedPage';
import SalesAgentPage from './pages/SalesAgentPage';
import BlueprintPage from './pages/BlueprintPage';
import TaskManagerPage from './pages/TaskManagerPage';
import DailyReportPage from './pages/DailyReportPage';
import CampaignTrackingPage from './pages/CampaignTrackingPage';
import ProposalPage from './pages/ProposalPage';
import PostSalePage from './pages/PostSalePage';
import DealStrategistPage from './pages/DealStrategistPage';
import FormatsPage from './pages/FormatsPage';
import PortalCredentialsPage from './pages/PortalCredentialsPage';
import WebhooksPage from './pages/WebhooksPage';
import PublicLandingPage from './pages/PublicLandingPage';
import { CircleNotch } from '@phosphor-icons/react';
import './App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <CircleNotch size={48} className="animate-spin text-blue-600" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Client users go to portal
  if (user.role === 'client') {
    return <ClientPortalPage />;
  }

  return <Layout>{children}</Layout>;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <CircleNotch size={48} className="animate-spin text-blue-600" />
      </div>
    );
  }

  if (user) {
    if (user.role === 'client') {
      return <Navigate to="/portal" replace />;
    }
    if (user.role === 'technician') {
      return <Navigate to="/tech-dashboard" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      
      {/* Payment success - accessible without layout */}
      <Route path="/payment-success" element={<PaymentSuccessPage />} />

      {/* Public quote signing page - no auth required */}
      <Route path="/quote/:quoteId" element={<QuoteSignPage />} />

      {/* Public Work Order view - no auth required */}
      <Route path="/work-order/:woId" element={<WorkOrderPublicPage />} />

      {/* Client Login - dedicated page */}
      <Route path="/client-login" element={
        <PublicRoute>
          <ClientLoginPage />
        </PublicRoute>
      } />

      {/* Client Portal */}
      <Route path="/portal" element={
        <ProtectedRoute>
          <ClientPortalPage />
        </ProtectedRoute>
      } />

      {/* Protected routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />

      <Route path="/leads" element={
        <ProtectedRoute>
          <LeadsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/clients" element={
        <ProtectedRoute>
          <ClientsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/clients/:id" element={
        <ProtectedRoute>
          <ClientDetailPage />
        </ProtectedRoute>
      } />
      
      <Route path="/quotes" element={
        <ProtectedRoute>
          <QuotesPage />
        </ProtectedRoute>
      } />
      
      <Route path="/quotes/:id" element={
        <ProtectedRoute>
          <QuoteDetailPage />
        </ProtectedRoute>
      } />
      
      <Route path="/payments" element={
        <ProtectedRoute>
          <PaymentsPage />
        </ProtectedRoute>
      } />

      <Route path="/payment-links" element={
        <ProtectedRoute>
          <PaymentLinksPage />
        </ProtectedRoute>
      } />

      <Route path="/invoices" element={
        <ProtectedRoute>
          <InvoicesPage />
        </ProtectedRoute>
      } />
      
      <Route path="/invoices/:id" element={
        <ProtectedRoute>
          <InvoiceDetailPage />
        </ProtectedRoute>
      } />

      <Route path="/work-orders" element={
        <ProtectedRoute>
          <WorkOrdersPage />
        </ProtectedRoute>
      } />
      
      <Route path="/work-orders/:id" element={
        <ProtectedRoute>
          <WorkOrderDetailPage />
        </ProtectedRoute>
      } />
      
      <Route path="/inventory" element={
        <ProtectedRoute>
          <InventoryPage />
        </ProtectedRoute>
      } />
      
      <Route path="/inventory/:id" element={
        <ProtectedRoute>
          <InventoryDetailPage />
        </ProtectedRoute>
      } />
      
      <Route path="/calendar" element={
        <ProtectedRoute>
          <CalendarPage />
        </ProtectedRoute>
      } />
      
      <Route path="/technicians" element={
        <ProtectedRoute>
          <TechniciansPage />
        </ProtectedRoute>
      } />

      <Route path="/catalog" element={
        <ProtectedRoute>
          <CatalogPage />
        </ProtectedRoute>
      } />

      <Route path="/payroll" element={
        <ProtectedRoute>
          <PayrollPage />
        </ProtectedRoute>
      } />

      <Route path="/documents" element={
        <ProtectedRoute>
          <DocumentsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/reports" element={
        <ProtectedRoute>
          <ReportsPage />
        </ProtectedRoute>
      } />

      <Route path="/users" element={
        <ProtectedRoute>
          <UserManagementPage />
        </ProtectedRoute>
      } />

      <Route path="/tech-dashboard" element={
        <ProtectedRoute>
          <TechDashboardPage />
        </ProtectedRoute>
      } />

      <Route path="/manual" element={
        <ProtectedRoute>
          <ManualPage />
        </ProtectedRoute>
      } />

      <Route path="/ai-assistant" element={
        <ProtectedRoute>
          <AIAssistantPage />
        </ProtectedRoute>
      } />

      <Route path="/outreach-safety" element={
        <ProtectedRoute>
          <OutreachSafetyPage />
        </ProtectedRoute>
      } />

      <Route path="/campaign-metrics" element={
        <ProtectedRoute>
          <CampaignMetricsPage />
        </ProtectedRoute>
      } />

      <Route path="/lead-gen-seo" element={
        <ProtectedRoute>
          <LeadGenSeoPage />
        </ProtectedRoute>
      } />

      <Route path="/pipeline" element={
        <ProtectedRoute>
          <PipelinePage />
        </ProtectedRoute>
      } />

      <Route path="/prospects" element={
        <ProtectedRoute>
          <ProspectsPage />
        </ProtectedRoute>
      } />

      <Route path="/prospects/:id" element={
        <ProtectedRoute>
          <ProspectDetailPage />
        </ProtectedRoute>
      } />

      <Route path="/call-candidates" element={
        <ProtectedRoute>
          <CallCandidatesPage />
        </ProtectedRoute>
      } />

      <Route path="/campaigns" element={
        <ProtectedRoute>
          <CampaignsPage />
        </ProtectedRoute>
      } />

      <Route path="/campaigns/:id" element={
        <ProtectedRoute>
          <CampaignDetailPage />
        </ProtectedRoute>
      } />

      <Route path="/b2b-reports" element={
        <ProtectedRoute>
          <B2BReportsPage />
        </ProtectedRoute>
      } />

      <Route path="/market-intel" element={
        <ProtectedRoute>
          <MarketIntelPage />
        </ProtectedRoute>
      } />

      <Route path="/projects" element={
        <ProtectedRoute>
          <ProjectsPage />
        </ProtectedRoute>
      } />

      <Route path="/projects/:id" element={
        <ProtectedRoute>
          <ProjectDetailPage />
        </ProtectedRoute>
      } />

      <Route path="/activity" element={
        <ProtectedRoute>
          <ActivityPage />
        </ProtectedRoute>
      } />

      <Route path="/gps-tracking" element={
        <ProtectedRoute>
          <GPSTrackingPage />
        </ProtectedRoute>
      } />

      <Route path="/booking" element={<BookingPage />} />

      <Route path="/hrms" element={
        <ProtectedRoute>
          <HRMSPage />
        </ProtectedRoute>
      } />

      <Route path="/executive" element={
        <ProtectedRoute>
          <ExecutiveDashboardPage />
        </ProtectedRoute>
      } />

      <Route path="/b2b-auto" element={
        <ProtectedRoute>
          <B2BAutoPage />
        </ProtectedRoute>
      } />

      <Route path="/b2b-hub" element={
        <ProtectedRoute>
          <B2BUnifiedPage />
        </ProtectedRoute>
      } />

      <Route path="/sales-agent" element={
        <ProtectedRoute>
          <SalesAgentPage />
        </ProtectedRoute>
      } />

      <Route path="/blueprints" element={
        <ProtectedRoute>
          <BlueprintPage />
        </ProtectedRoute>
      } />

      <Route path="/task-manager" element={
        <ProtectedRoute>
          <TaskManagerPage />
        </ProtectedRoute>
      } />

      <Route path="/daily-report" element={
        <ProtectedRoute>
          <DailyReportPage />
        </ProtectedRoute>
      } />

      <Route path="/campaign-tracking" element={
        <ProtectedRoute>
          <CampaignTrackingPage />
        </ProtectedRoute>
      } />

      <Route path="/proposals" element={
        <ProtectedRoute>
          <ProposalPage />
        </ProtectedRoute>
      } />

      <Route path="/post-sale" element={
        <ProtectedRoute>
          <PostSalePage />
        </ProtectedRoute>
      } />

      <Route path="/deal-strategy" element={
        <ProtectedRoute>
          <DealStrategistPage />
        </ProtectedRoute>
      } />

      <Route path="/formats" element={
        <ProtectedRoute>
          <FormatsPage />
        </ProtectedRoute>
      } />

      <Route path="/portal-credentials" element={
        <ProtectedRoute>
          <PortalCredentialsPage />
        </ProtectedRoute>
      } />

      <Route path="/webhooks" element={
        <ProtectedRoute>
          <WebhooksPage />
        </ProtectedRoute>
      } />

      {/* Default redirect */}
      <Route path="/" element={<PublicLandingPage />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
